import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Modal, TextField, Button,
  IconButton, Avatar, Stack, CircularProgress, Chip, Menu, MenuItem,
  LinearProgress, Snackbar, Alert
} from '@mui/material';
import { Close, AddPhotoAlternate, VideoLibrary, PhotoCamera, Videocam, ArrowDropDown, Send } from '@mui/icons-material';
import { db, auth, storage } from './firebase'; // Asegúrate de tener storage configurado
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useTranslatedDictionary } from './hooks/useTranslatedDictionary';

const PUBLICAR_TEXT = {
  es: {
    newPost: 'Nueva Publicación',
    placeholder: '¿Qué está pasando en tu línea?',
    selectedFiles: 'Archivos seleccionados',
    textBackground: 'Fondo del texto',
    preview: 'Vista previa',
    noBackground: 'Sin fondo',
    video: 'Video',
    image: 'Imagen',
    upload: 'Subir',
    photos: 'Fotos',
    videos: 'Videos',
    cameraPhoto: 'Camara',
    recordVideo: 'Grabar video',
    publish: 'Publicar',
    publishing: 'Publicando...',
    uploadError: 'No se pudo publicar. Intenta de nuevo.',
    bgOcean: 'Océano',
    bgSunset: 'Atardecer',
    bgViolet: 'Violeta',
    bgMidnight: 'Medianoche',
    bgLime: 'Lima',
  },
  en: {
    newPost: 'New Post',
    placeholder: 'What is happening in your feed?',
    selectedFiles: 'Selected files',
    textBackground: 'Text background',
    preview: 'Preview',
    noBackground: 'No background',
    video: 'Video',
    image: 'Image',
    upload: 'Upload',
    photos: 'Photos',
    videos: 'Videos',
    cameraPhoto: 'Camera',
    recordVideo: 'Record video',
    publish: 'Post',
    publishing: 'Posting...',
    uploadError: 'Could not publish. Please try again.',
    bgOcean: 'Ocean',
    bgSunset: 'Sunset',
    bgViolet: 'Violet',
    bgMidnight: 'Midnight',
    bgLime: 'Lime',
  },
};

const TEXT_BACKGROUND_OPTIONS = [
  {
    id: 'none',
    background: 'transparent',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  {
    id: 'ocean',
    labelKey: 'bgOcean',
    background: 'linear-gradient(135deg, rgba(0,212,255,0.26) 0%, rgba(0,69,255,0.38) 100%)',
    color: '#effcff',
    border: '1px solid rgba(0,212,255,0.24)',
  },
  {
    id: 'sunset',
    labelKey: 'bgSunset',
    background: 'linear-gradient(135deg, rgba(255,128,0,0.32) 0%, rgba(255,69,96,0.34) 100%)',
    color: '#fff6ef',
    border: '1px solid rgba(255,140,80,0.24)',
  },
  {
    id: 'violet',
    labelKey: 'bgViolet',
    background: 'linear-gradient(135deg, rgba(126,87,255,0.32) 0%, rgba(64,196,255,0.24) 100%)',
    color: '#f7f3ff',
    border: '1px solid rgba(126,87,255,0.24)',
  },
  {
    id: 'midnight',
    labelKey: 'bgMidnight',
    background: 'linear-gradient(135deg, rgba(18,23,35,0.96) 0%, rgba(41,54,76,0.96) 100%)',
    color: '#ffffff',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  {
    id: 'lime',
    labelKey: 'bgLime',
    background: 'linear-gradient(135deg, rgba(103,220,106,0.32) 0%, rgba(0,212,255,0.22) 100%)',
    color: '#f4fff4',
    border: '1px solid rgba(103,220,106,0.24)',
  },
];

const Publicar = ({ open, handleClose, onSuccess, language = 'es', themeMode = 'oscuro' }) => {
  const [text, setText] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [textBackground, setTextBackground] = useState('none');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [cameraMenuAnchor, setCameraMenuAnchor] = useState(null);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const cameraPhotoInputRef = useRef(null);
  const cameraVideoInputRef = useRef(null);
  const t = useTranslatedDictionary(PUBLICAR_TEXT, language);
  const isLightTheme = themeMode === 'claro';
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const themedBackgroundOptions = TEXT_BACKGROUND_OPTIONS.map((option) =>
    option.id === 'none' && isLightTheme
      ? { ...option, color: onSurface(1), border: `1px solid ${onSurface(0.12)}` }
      : option
  );
  const selectedBackground = themedBackgroundOptions.find((option) => option.id === textBackground) || themedBackgroundOptions[0];

  const user = auth.currentUser;
  const [resolvedPhoto, setResolvedPhoto] = useState(user?.photoURL || '');

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.photoURL) setResolvedPhoto(data.photoURL);
      }
    }).catch(() => {});
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const newItems = files.map((file) => ({
      id: `${file.name}_${file.lastModified}_${Math.random().toString(36).slice(2)}`,
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('video/') ? 'video' : 'image',
    }));

    setMediaItems((prev) => [...prev, ...newItems]);
    e.target.value = '';
  };

  const removeMediaItem = (id) => {
    setMediaItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      const removed = prev.find((item) => item.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return next;
    });
  };

  const closeCameraMenu = () => setCameraMenuAnchor(null);
  const closeUploadMenu = () => setUploadMenuAnchor(null);

  const overallProgress = mediaItems.length
    ? Math.round(
        mediaItems.reduce((sum, item) => sum + (uploadProgress[item.id] || 0), 0) /
          mediaItems.length
      )
    : 0;

  const uploadItem = (item, uid) =>
    new Promise((resolve, reject) => {
      const storageRef = ref(
        storage,
        `posts/${uid}/${Date.now()}_${item.file.name}`
      );
      const task = uploadBytesResumable(storageRef, item.file, {
        contentType: item.file.type,
      });
      task.on(
        'state_changed',
        (snapshot) => {
          const pct = snapshot.totalBytes
            ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            : 0;
          setUploadProgress((prev) => ({ ...prev, [item.id]: pct }));
        },
        (error) => reject(error),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            setUploadProgress((prev) => ({ ...prev, [item.id]: 100 }));
            resolve({ type: item.type, url, name: item.file.name });
          } catch (err) {
            reject(err);
          }
        }
      );
    });

  const handlePost = async () => {
    if (!text && mediaItems.length === 0) return;
    if (!user) return;
    setLoading(true);
    setErrorMsg('');
    setUploadProgress({});

    try {
      // Leer perfil actualizado desde Firestore
      let username = user.displayName || "";
      let userImg = user.photoURL || "";
      let handle = "";
      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          username = data.displayName || username;
          userImg = data.photoURL || userImg;
          handle = data.username || "";
        }
      } catch {
        // Si falla, usamos los datos de auth
      }
      if (!username) username = user.email.split('@')[0];
      if (!handle) handle = user.email.split('@')[0] || "usuario";

      const media = await Promise.all(
        mediaItems.map((item) => uploadItem(item, user.uid))
      );
      let imageUrl = "";
      let videoUrl = "";
      media.forEach((m) => {
        if (!imageUrl && m.type === 'image') imageUrl = m.url;
        if (!videoUrl && m.type === 'video') videoUrl = m.url;
      });

      // 2. Guardar post en Firestore
      await addDoc(collection(db, "posts"), {
        uid: user.uid,
        username,
        handle,
        userImg,
        text: text,
        textBackground,
        media,
        imageUrl: imageUrl,
        videoUrl: videoUrl,
        likes: [],
        savedBy: [],
        commentsCount: 0,
        timestamp: serverTimestamp()
      });

      // Limpiar y cerrar
      setText('');
      setTextBackground('none');
      mediaItems.forEach((item) => item.preview && URL.revokeObjectURL(item.preview));
      setMediaItems([]);
      setUploadProgress({});
      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error al publicar:", error);
      setErrorMsg(error?.code === 'storage/unauthorized'
        ? t('uploadError')
        : (error?.message || t('uploadError')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} data-swipe-modal="publicar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box sx={{
        width: '100%', maxWidth: 500, bgcolor: isLightTheme ? 'rgba(255,255,255,0.97)' : 'rgba(20,20,20,0.9)',
        backdropFilter: 'blur(15px)', borderRadius: '24px',
        p: 3, outline: 'none'
      }}>

        {/* Header Modal */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ color: onSurface(1), fontWeight: 600 }}>{t('newPost')}</Typography>
          <IconButton onClick={handleClose} sx={{ color: onSurface(1) }}><Close /></IconButton>
        </Stack>

        <Stack direction="row" spacing={2} mb={2}>
          <Avatar src={resolvedPhoto} />
          <TextField
            fullWidth multiline rows={4} placeholder={t('placeholder')}
            variant="standard" value={text} onChange={(e) => setText(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { color: onSurface(1), fontSize: '1.1rem' } }}
          />
        </Stack>

        <Stack spacing={1.2} sx={{ mb: 2 }}>
          <Typography sx={{ color: onSurface(0.74), fontSize: '0.9rem', fontWeight: 600 }}>
            {t('textBackground')}
          </Typography>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {themedBackgroundOptions.map((option) => {
              const isSelected = option.id === textBackground;
              return (
                <Button
                  key={option.id}
                  onClick={() => setTextBackground(option.id)}
                  variant={isSelected ? 'contained' : 'outlined'}
                  size="small"
                  sx={{
                    minWidth: 0,
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 1.5,
                    py: 0.8,
                    border: isSelected ? '1px solid #00d4ff' : option.border,
                    boxShadow: isSelected ? '0 0 0 1px #00d4ff' : 'none',
                    color: onSurface(1),
                    background: isSelected
                      ? option.id === 'none' ? 'rgba(0,212,255,0.14)' : option.background
                      : onSurface(0.02),
                    '&:hover': {
                      border: '1px solid #00d4ff',
                      background: isSelected
                        ? option.id === 'none' ? 'rgba(0,212,255,0.14)' : option.background
                        : 'rgba(0,212,255,0.1)',
                    },
                  }}
                >
                  {option.id === 'none' ? t('noBackground') : t(option.labelKey)}
                </Button>
              );
            })}
          </Stack>

          {text && (
            <Stack spacing={0.8}>
              <Typography sx={{ color: onSurface(0.56), fontSize: '0.78rem' }}>
                {t('preview')}
              </Typography>
              <Box
                sx={{
                  borderRadius: '18px',
                  p: 2,
                  minHeight: 110,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  background: selectedBackground.background,
                  color: selectedBackground.color,
                  border: selectedBackground.border,
                  boxShadow: textBackground !== 'none' ? '0 16px 36px rgba(0,0,0,0.24)' : 'none',
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.55,
                  }}
                >
                  {text}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        {mediaItems.length > 0 && (
          <Stack spacing={1.2} sx={{ mb: 2 }}>
            <Typography sx={{ color: onSurface(0.7), fontSize: '0.9rem' }}>
              {t('selectedFiles')} ({mediaItems.length})
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {mediaItems.map((item) => {
                const progress = uploadProgress[item.id];
                const isUploading = loading && progress !== undefined && progress < 100;
                return (
                  <Box key={item.id} sx={{ position: 'relative', width: 110, height: 110 }}>
                    <Box
                      component={item.type === 'video' ? 'video' : 'img'}
                      src={item.preview}
                      controls={item.type === 'video'}
                      sx={{ width: '100%', height: '100%', borderRadius: '12px', objectFit: 'cover', bgcolor: '#000' }}
                    />
                    <Chip
                      size="small"
                        label={item.type === 'video' ? t('video') : t('image')}
                      sx={{ position: 'absolute', top: 6, left: 6, bgcolor: 'rgba(0,0,0,0.7)', color: '#fff' }}
                    />
                    {!loading && (
                      <IconButton
                        size="small"
                        onClick={() => removeMediaItem(item.id)}
                        sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.7)', color: '#fff' }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                    {isUploading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: '12px',
                          bgcolor: 'rgba(0,0,0,0.45)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <CircularProgress
                          variant="determinate"
                          value={progress}
                          size={36}
                          sx={{ color: '#00d4ff' }}
                        />
                        <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 600 }}>
                          {progress}%
                        </Typography>
                      </Box>
                    )}
                    {loading && progress === 100 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: '12px',
                          bgcolor: 'rgba(0,0,0,0.25)',
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        )}

        {/* Acciones */}
        <Stack spacing={1.2} sx={{ pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 1,
              borderRadius: '18px',
              bgcolor: onSurface(0.03),
              border: `1px solid ${onSurface(0.06)}`,
            }}
          >
          <Button
            onClick={(event) => setUploadMenuAnchor(event.currentTarget)}
            startIcon={<AddPhotoAlternate />}
            endIcon={<ArrowDropDown />}
            sx={{
              flex: '1 1 130px',
              color: '#00d4ff',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              justifyContent: 'flex-start',
            }}
            size="small"
          >
            {t('upload')}
          </Button>

          <Button
            onClick={(event) => setCameraMenuAnchor(event.currentTarget)}
            startIcon={<PhotoCamera />}
            endIcon={<ArrowDropDown />}
            sx={{
              flex: '1 1 130px',
              color: '#00d4ff',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              justifyContent: 'flex-start',
            }}
            size="small"
          >
            {t('cameraPhoto')}
          </Button>

          <Menu
            anchorEl={uploadMenuAnchor}
            open={Boolean(uploadMenuAnchor)}
            onClose={closeUploadMenu}
            PaperProps={{
              sx: {
                mt: 1,
                bgcolor: isLightTheme ? 'rgba(255,255,255,0.98)' : 'rgba(10,15,20,0.98)',
                color: onSurface(1),
                border: `1px solid ${onSurface(0.08)}`,
                borderRadius: '14px',
                overflow: 'hidden',
              },
            }}
          >
            <MenuItem
              onClick={() => { closeUploadMenu(); photoInputRef.current?.click(); }}
              sx={{ gap: 1.25, py: 1.2 }}
            >
              <AddPhotoAlternate fontSize="small" sx={{ color: '#00d4ff' }} />
              {t('photos')}
            </MenuItem>
            <MenuItem
              onClick={() => { closeUploadMenu(); videoInputRef.current?.click(); }}
              sx={{ gap: 1.25, py: 1.2 }}
            >
              <VideoLibrary fontSize="small" sx={{ color: '#00d4ff' }} />
              {t('videos')}
            </MenuItem>
          </Menu>

          <Menu
            anchorEl={cameraMenuAnchor}
            open={Boolean(cameraMenuAnchor)}
            onClose={closeCameraMenu}
            PaperProps={{
              sx: {
                mt: 1,
                bgcolor: isLightTheme ? 'rgba(255,255,255,0.98)' : 'rgba(10,15,20,0.98)',
                color: onSurface(1),
                border: `1px solid ${onSurface(0.08)}`,
                borderRadius: '14px',
                overflow: 'hidden',
              },
            }}
          >
            <MenuItem
              onClick={() => { closeCameraMenu(); cameraPhotoInputRef.current?.click(); }}
              sx={{ gap: 1.25, py: 1.2 }}
            >
              <PhotoCamera fontSize="small" sx={{ color: '#00d4ff' }} />
              {t('cameraPhoto')}
            </MenuItem>
            <MenuItem
              onClick={() => { closeCameraMenu(); cameraVideoInputRef.current?.click(); }}
              sx={{ gap: 1.25, py: 1.2 }}
            >
              <Videocam fontSize="small" sx={{ color: '#00d4ff' }} />
              {t('recordVideo')}
            </MenuItem>
          </Menu>

          <input
            ref={photoInputRef}
            type="file"
            hidden
            accept="image/*"
            multiple
            onChange={handleMediaChange}
          />
          <input
            ref={videoInputRef}
            type="file"
            hidden
            accept="video/*"
            multiple
            onChange={handleMediaChange}
          />
          <input
            ref={cameraPhotoInputRef}
            type="file"
            hidden
            accept="image/*"
            capture="environment"
            onChange={handleMediaChange}
          />
          <input
            ref={cameraVideoInputRef}
            type="file"
            hidden
            accept="video/*"
            capture="environment"
            onChange={handleMediaChange}
          />
          </Box>

          {loading && mediaItems.length > 0 && (
            <Stack spacing={0.6}>
              <LinearProgress
                variant="determinate"
                value={overallProgress}
                sx={{
                  height: 6,
                  borderRadius: '999px',
                  bgcolor: onSurface(0.08),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: '999px',
                    background: 'linear-gradient(45deg, #00d4ff 30%, #0045ff 90%)',
                  },
                }}
              />
              <Typography sx={{ color: onSurface(0.6), fontSize: '0.75rem', textAlign: 'right' }}>
                {overallProgress}%
              </Typography>
            </Stack>
          )}

          <Button
            onClick={handlePost}
            disabled={loading || (!text && mediaItems.length === 0)}
            variant="contained"
            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
            fullWidth
            sx={{
              borderRadius: '12px',
              px: 2.5,
              fontWeight: 600,
              background: 'linear-gradient(45deg, #00d4ff 30%, #0045ff 90%)',
              whiteSpace: 'nowrap',
            }}
          >
            {loading
              ? `${t('publishing')}${mediaItems.length > 0 ? ` ${overallProgress}%` : ''}`
              : t('publish')}
          </Button>
        </Stack>

        <Snackbar
          open={Boolean(errorMsg)}
          autoHideDuration={5000}
          onClose={() => setErrorMsg('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setErrorMsg('')} severity="error" variant="filled" sx={{ borderRadius: '12px' }}>
            {errorMsg}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  );
};

export default Publicar;